"use client";

import Image from "next/image";
import { useState } from "react";
import { getModelLatestUpdates } from "@/app/utilities/apis";
import type { LatestUpdateItem } from "@/app/utilities/interfaces";

const PAGE_SIZE = 5;

interface DisplayUpdate {
  id: number;
  date: string;
  isLatest: boolean;
  description: string;
}

interface LatestUpdatesProps {
  modelId?: string;
  data: {
    title: string;
    updates: DisplayUpdate[];
  };
}

const mapToDisplay = (items: LatestUpdateItem[], startIndex: number): DisplayUpdate[] =>
  items.map((item, i) => ({
    id: startIndex + i + 1,
    date: new Date(item.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    isLatest: startIndex + i === 0,
    description: item.content,
  }));

const LatestUpdates = ({ modelId, data }: LatestUpdatesProps) => {
  const [updates, setUpdates] = useState<DisplayUpdate[]>(data.updates);
  const [page, setPage] = useState(1);
  // The model-detail response ships a capped preview (PAGE_SIZE items) inline;
  // a full page means there's likely more to fetch — confirmed/corrected once
  // the next page comes back short.
  const [hasMore, setHasMore] = useState(data.updates.length >= PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const loadMore = async () => {
    if (!modelId || loading) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const res = await getModelLatestUpdates(modelId, nextPage, PAGE_SIZE);
      const items = res?.data || [];
      if (items.length > 0) {
        setUpdates((prev) => [...prev, ...mapToDisplay(items, prev.length)]);
        setPage(nextPage);
      }
      if (items.length < PAGE_SIZE) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-[32px] overflow-hidden shadow-sm">
      <div className="p-4 md:p-6 border-b border-gray-100">
        <h2 className="text-xl md:text-2xl font-bold text-[#1a202c]">
          {data.title}
        </h2>
      </div>

      <div className="px-4 pt-4 pb-0 md:px-6 md:pt-6 md:pb-0">
        {updates.map((item, index) => (
          <div key={item.id} className="relative flex gap-4 md:gap-6">
            {/* Timeline Column */}
            <div className="flex flex-col items-center">
              <div className="w-[24px] h-[24px] rounded-full bg-[#2d3748] border-4 border-[#E8E8E8] z-10 shrink-0" />
              {index !== updates.length - 1 && (
                <div className="w-0 h-full border-l-2 border-dashed border-gray-300 my-1" />
              )}
            </div>

            {/* Content Column */}
            <div className="pb-8 flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[12px] md:text-[14px] font-semibold tex-[#595959] md:text-[#667085]">
                  {item.date}
                </span>
                {item.isLatest && (
                  <span className="bg-[#F9F9F9] text-[#475569] text-[10px] px-2 py-0.5 rounded-[20px] font-bold">
                    Latest
                  </span>
                )}
              </div>
              <div
                className="text-[12px] md:text-[14px] text-[#667085] md:text-[#373737] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loading}
          className="w-full bg-[#F9F9F9] py-4 flex justify-center items-center gap-2 text-[#008C8B] font-bold border-t border-gray-100 transition-colors hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Loading..." : "Load More"}
          {!loading && (
            <span className="inline-block rotate-90">
              <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                alt="view-all-arrow-icon"
                title="view all arrow"
                width={20}
                height={20}
                className="[filter:brightness(0)_saturate(100%)_invert(39%)_sepia(77%)_saturate(876%)_hue-rotate(137deg)_brightness(93%)_contrast(101%)]"
              />
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export default LatestUpdates;
